import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";
import { Book } from "@bitwarden/web-vault/app/models/data/blobby/book.data";
import { GlossNumber } from "@bitwarden/web-vault/app/models/data/shared/gloss-number";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { InstitutionAccountLink } from "@bitwarden/web-vault/app/models/types/institution.type";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { SyncStatusView } from "@bitwarden/web-vault/app/models/view/sync-status/sync-status.view";

export type NewAccountActions = "link" | "rename" | "default";
export type UnrecognizedAccount = {
  _id?: string;
  currencies: string[];
  source: string;
  accountId: string;
  action: NewAccountActions;
  selectedCurrency: string;
  selectedTimeZone: string;
  institutionAccountLink: InstitutionAccountLink;
  origin: Origin;
};

export type AccountBalance = {
  account: Book;
  accountBalance: GlossNumber;
};

export type BasiqTypes =
  | "transaction"
  | "savings"
  | "credit-card"
  | "mortgage"
  | "loan"
  | "investment"
  | "term-deposit"
  | "insurance"
  | "foreign"
  | "unknown";
export type PlaidTypes =
  | "depository"
  | "credit"
  | "loan"
  | "mortgage"
  | "investment"
  | "insurance"
  | "unknown";
// export type AccountManualType = "regular";
export type AccountManualType = "bank" | "credit" | "stock" | "other";
// TODO: to be improved
export const accountManualType = {
  bank: "bank",
  credit: "credit",
  stock: "stock",
  other: "other",
} as const;

export type AccountType = BasiqTypes | PlaidTypes | AccountManualType;

export const accountTypeTitleMap: Record<AccountManualType, string> = {
  bank: "addNewBankOrSavingsAccount",
  credit: "addNewCreditAccount",
  stock: "addNewInvestmentAccount", // internal type is "stock"; user-facing label is "Investment"
  other: "addNewOtherAccount",
};

export type BalanceForm = {
  balance: string;
  currency: string;
  date: string;
  transactionStatus: TransactionStatusEnum;
  description?: string;
  time?: string;
  type?: "Opening" | "Closing";
};

export type TransactionForm = {
  // NB `amount`/`quantity` are overloaded by account type (see
  // edit-transaction-details.convertTransactionDetailToTransactionForm):
  //  - cash accounts (bank/credit): `amount` is the transaction's money value;
  //    `quantity` is unused (mirrors `amount` on add; on edit it holds the transaction's
  //    `valuationPrice`, which is null/undefined on this path — nothing ever sets it).
  //  - unit-priced accounts (stock/other): `amount` is the unit price (valuationPrice);
  //    `quantity` is the unit count (actualQuantity.amount).
  amount: number;
  quantity: number;
  // For unit-priced accounts this is the instrument symbol (e.g. "BHP"); for cash
  // accounts it is the money symbol of the amount — usually the account currency,
  // but it can differ (e.g. a USD transaction in an AUD account via CSV import; see #4590).
  symbol?: string;
  currency: string;
  categoryId?: string;
  categoryName?: string;
  date: string;
  transactionStatus: TransactionStatusEnum;
  description?: string;
  time?: string;
  type: TransactionDirection;
};

export type AccountTableView = {
  original: AccountView;
  country: string;
  institution: string;
  id: string;
  dragHandle: boolean;
  rightChevron: boolean;
  institutionCode: string;
  name: string;
  interestRate: number;
  balance: number;
  symbol: string;
  type: AccountType;
  statusPoint: SyncStatusView;
  full_logo_url: string;
  square_logo_url: string;
  currency: string;
  accountProductName: string;
};

export type SymbolTableView = {
  code: string;
  amount: number;
  convertedAmount: number;
  flag: string;
};

export type dialogDataType = {
  accountView: AccountView;
  closeDialog: CallableFunction;
  actionSucceeded: CallableFunction;
  isFromGuide: boolean;
  initialBalance?: number;
  initialDate?: string;
  initialCurrency?: string;
  initialDescription?: string;
  initialType?: string;
};

export type ProgressStatus = "complete" | "interrupted" | "empty";
